import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SignIn, useUser } from '@clerk/clerk-react'
import { useEnablerSubmission } from '@/hooks/useEnablerSubmission'

interface RequireEnablerSubmissionProps {
  children: React.ReactNode
}

/**
 * Route protection component for provider-only pages
 * Redirects users to /get-started if they haven't submitted the enabler form
 * Allows access for pending, approved, and rejected submissions
 */
export function RequireEnablerSubmission({ children }: RequireEnablerSubmissionProps) {
  const { user, isLoaded: isUserLoaded } = useUser()
  const { hasSubmitted, isLoading: isSubmissionLoading } = useEnablerSubmission()
  const navigate = useNavigate()

  const isLoading = !isUserLoaded || isSubmissionLoading

  // User is signed in but hasn't submitted the enabler form - redirect to /get-started
  // IMPORTANT: This useEffect must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (!isLoading && user && !hasSubmitted) {
      navigate('/get-started', { replace: true })
    }
  }, [hasSubmitted, isLoading, user, navigate])

  // Still loading Clerk or checking submission status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Not signed in - show Clerk sign-in
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">Provider Access Required</h1>
            <p className="text-muted-foreground">Please sign in to continue</p>
          </div>
          <SignIn
            routing="path"
            path="/sign-in"
            redirectUrl="/provider"
          />
        </div>
      </div>
    )
  }

  // User is signed in but hasn't submitted - show null while redirecting
  if (!hasSubmitted) {
    return null
  }

  // User has submitted (any status: pending, approved, or rejected) - render protected content
  return <>{children}</>
}
